import { ApiResponse, ApiError, Appointment4D, Staff4D, Patient4D, Provider4D, FacilityLocation4D, Procedure4D, AppointmentType4D, CreatePatient4DRequest, CreatePatient4DResponse, PatientChargeModelVM, StaffService4D, ProcedurePerformed4D } from '../types';
import { supabase } from '../lib/supabase';

export class FourDApi {
  private clientId: string;
  private clientSecret: string;
  private subscriptionKey: string;
  private endpointUrl: string;

  constructor(credentials: {
    clientId: string;
    clientSecret: string;
    subscriptionKey: string;
    endpointUrl?: string;
  }) {
    this.clientId = credentials.clientId;
    this.clientSecret = credentials.clientSecret;
    this.subscriptionKey = credentials.subscriptionKey;
    this.endpointUrl = credentials.endpointUrl || 'https://api.4d-emr.com/api/public';
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers = {
      'client-id': this.clientId,
      'client-secret': this.clientSecret,
      'subscription-key': this.subscriptionKey,
      'Accept': 'application/json',
      'Cache-Control': 'no-cache',
    };

    const response = await fetch(`${this.endpointUrl}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error: ApiError = {
        message: 'API request failed',
        status: response.status,
      };

      try {
        const errorData = await response.json();
        error.message = errorData.message || error.message;
        error.code = errorData.code;
      } catch {
        // Use default error message if response is not JSON
      }

      throw error;
    }

    return response.json();
  }

  // Appointments
  async getAppointments(params?: {
    startDate?: string;
    endDate?: string;
    facilityLocationId?: number;
    patientId?: number;
    staffId?: number;
  }): Promise<ApiResponse<Appointment4D>> {
    const queryParams = new URLSearchParams();
    
    if (params?.startDate) {
      queryParams.append('startDate', params.startDate);
    }
    if (params?.endDate) {
      queryParams.append('endDate', params.endDate);
    }
    if (params?.facilityLocationId) {
      queryParams.append('facilityLocationId', params.facilityLocationId.toString());
    }
    if (params?.patientId) {
      queryParams.append('patientId', params.patientId.toString());
    }
    if (params?.staffId) {
      queryParams.append('staffId', params.staffId.toString());
    }

    const queryString = queryParams.toString();
    const endpoint = `/appointments${queryString ? `?${queryString}` : ''}`;

    return this.request<ApiResponse<Appointment4D>>(endpoint);
  }
  // Appointment Types
  async getAppointmentTypes(): Promise<AppointmentType4D[]> {
    try {
      const response = await fetch(`${this.endpointUrl}/appointmenttypes`, {
        headers: {
          'client-id': this.clientId,
          'client-secret': this.clientSecret,
          'subscription-key': this.subscriptionKey,
          'Accept': '*/*',
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        const error: ApiError = {
          message: 'Failed to fetch appointment types',
          status: response.status,
        };

        try {
          const errorData = await response.json();
          error.message = errorData.message || error.message;
          error.code = errorData.code;
        } catch {
          // Use default error message if response is not JSON
        }

        throw error;
      }

      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error('Invalid appointment types response format');
      }

      return data;
    } catch (error) {
      console.error('Error fetching appointment types:', error);
      throw error;
    }
  }

  // Staff
  async getStaff(params?: {
    isActive?: boolean;
  }): Promise<Staff4D[]> {
    const queryParams = new URLSearchParams();
    
    if (params?.isActive !== undefined) {
      queryParams.append('isActive', params.isActive.toString());
    }

    const queryString = queryParams.toString();
    const endpoint = `/staff${queryString ? `?${queryString}` : ''}`;

    return this.request<Staff4D[]>(endpoint);
  }

  // Staff Services
  async getStaffServices(staffId: number): Promise<ApiResponse<StaffService4D>> {
    return this.request<ApiResponse<StaffService4D>>(`/staffservices?staffId=${staffId}`);
  }

  // Patients
  async getPatients(params?: {
    firstName?: string;
    lastName?: string;
    birthdate?: string;
    phone?: string;
    email?: string;
    zipcode?: string;
    accountNumber?: string;
    page?: number;
    pageSize?: number;
  }): Promise<ApiResponse<Patient4D>> {
    const queryParams = new URLSearchParams();
    
    if (params?.firstName) {
      queryParams.append('firstname', params.firstName);
    }
    if (params?.lastName) {
      queryParams.append('lastname', params.lastName);
    }
    if (params?.birthdate) {
      queryParams.append('birthdate', params.birthdate);
    }
    if (params?.phone) {
      queryParams.append('phone', params.phone);
    }
    if (params?.email) {
      queryParams.append('email', params.email);
    }
    if (params?.zipcode) {
      queryParams.append('zipcode', params.zipcode);
    }
    if (params?.accountNumber) {
      queryParams.append('accountNumber', params.accountNumber);
    }
    if (params?.page) {
      queryParams.append('page.skip', ((params.page - 1) * (params.pageSize || 10)).toString());
      queryParams.append('page.count', (params.pageSize || 10).toString());
      queryParams.append('page.needTotalCount', 'true');
    }

    const queryString = queryParams.toString();
    const endpoint = `/patients${queryString ? `?${queryString}` : ''}`;

    return this.request<ApiResponse<Patient4D>>(endpoint);
  }

  // Get all patients by looping through pages
  async getAllPatients(params?: {
    fromModifiedDate?: string;
    toModifiedDate?: string;
  }): Promise<Patient4D[]> {
    let allPatients: Patient4D[] = [];
    let skip = 0;
    const count = 1000; // As per recommendation
    let hasMore = true;

    while (hasMore) {
      const queryParams = new URLSearchParams();
      queryParams.append('page.skip', skip.toString());
      queryParams.append('page.count', count.toString());
      queryParams.append('page.needTotalCount', 'true');

      if (params?.fromModifiedDate) {
        queryParams.append('fromModifiedDate', params.fromModifiedDate);
      }
      if (params?.toModifiedDate) {
        queryParams.append('toModifiedDate', params.toModifiedDate);
      }

      const queryString = queryParams.toString();
      const endpoint = `/patients${queryString ? `?${queryString}` : ''}`;
      
      try {
        const response = await this.request<ApiResponse<Patient4D>>(endpoint);
        
        if (response.Items && response.Items.length > 0) {
          allPatients = allPatients.concat(response.Items);
          skip += response.Items.length;
          // If we get less than the count, we are on the last page
          if (response.Items.length < count) {
            hasMore = false;
          }
        } else {
          // No more items, stop looping
          hasMore = false;
        }
      } catch (error) {
        console.error(`Error fetching page with skip=${skip}:`, error);
        // Depending on desired error handling, you might want to stop or retry
        hasMore = false; 
      }
    }

    return allPatients;
  }

  // Get Patients Added (new method)
  async getPatientsAdded(params: {
    beginDate: string;
    endDate: string;
  }): Promise<ApiResponse<Patient4D>> {
    const queryParams = new URLSearchParams();
    queryParams.append('beginDate', params.beginDate);
    queryParams.append('endDate', params.endDate);
    
    const queryString = queryParams.toString();
    const endpoint = `/patients/added${queryString ? `?${queryString}` : ''}`;

    return this.request<ApiResponse<Patient4D>>(endpoint);
  }

  // Get Patients Modified (new method)
  async getPatientsModified(params: {
    beginDate: string;
    endDate: string;
  }): Promise<ApiResponse<Patient4D>> {
    const queryParams = new URLSearchParams();
    queryParams.append('beginDate', params.beginDate);
    queryParams.append('endDate', params.endDate);
    
    const queryString = queryParams.toString();
    const endpoint = `/patients/modified${queryString ? `?${queryString}` : ''}`;

    return this.request<ApiResponse<Patient4D>>(endpoint);
  }

  // Create Patient
  async createPatient(data: CreatePatient4DRequest): Promise<CreatePatient4DResponse> {
    return this.request<CreatePatient4DResponse>('/patients/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...data,
        CoordinatorId: data.CoordinatorId || 1, // Default to staff ID 1 if not provided
      }),
    });
  }

  // Get Single Patient
  async getPatient(patientId: number): Promise<Patient4D> {
    return this.request<Patient4D>(`/patients/${patientId}`);
  }

  // Get Procedures
  async getProcedures(providerId?: number): Promise<Procedure4D[]> {
    const queryParams = new URLSearchParams();
    
    if (providerId) {
      queryParams.append('providerId', providerId.toString());
    }

    const queryString = queryParams.toString();
    const endpoint = `/procedures${queryString ? `?${queryString}` : ''}`;

    return this.request<Procedure4D[]>(endpoint);
  }

  // Get Providers
  async getProviders(): Promise<Provider4D[]> {
    return this.request<Provider4D[]>('/providers');
  }

  // Get Single Provider
  async getProvider(providerId: number): Promise<Provider4D> {
    return this.request<Provider4D>(`/providers/${providerId}`);
  }

  // Get Facility Locations
  async getFacilityLocations(): Promise<FacilityLocation4D[]> {
    return this.request<FacilityLocation4D[]>('/facilitylocations');
  }

  // Get Single Facility Location
  async getFacilityLocation(facilityId: number): Promise<FacilityLocation4D> {
    return this.request<FacilityLocation4D>(`/facilitylocations/${facilityId}`);
  }

  // Get Patient Charges
  async getPatientCharges(params: {
    beginDate: string;
    endDate: string;
  }): Promise<PatientChargeModelVM[]> {
    const queryParams = new URLSearchParams({
      beginDate: params.beginDate,
      endDate: params.endDate
    });

    return this.request<PatientChargeModelVM[]>(
      `/reports/charges?${queryParams.toString()}`
    );
  }

  // Get Procedures Performed by Date Range (Reports)
  async getProceduresPerformed(params: { beginDate: string; endDate: string; }): Promise<ProcedurePerformed4D[]> {
    const queryParams = new URLSearchParams({
      beginDate: params.beginDate,
      endDate: params.endDate,
    });
    return this.request<ProcedurePerformed4D[]>(`/reports/procedures?${queryParams.toString()}`);
  }

  // Get all appointments by looping through pages
  async getAllAppointments(params?: {
    fromDate?: string;
    toDate?: string;
    fromModifiedDate?: string;
    toModifiedDate?: string;
    typeId?: number;
    providerId?: number;
    patientId?: number;
    status?: string[];
  }): Promise<Appointment4D[]> {
    let allAppointments: Appointment4D[] = [];
    let skip = 0;
    const count = 999; // Maximize page size per provider guidance
    let hasMore = true;

    while (hasMore) {
      const queryParams = new URLSearchParams();
      // Use options.* only when filtering by modified date; otherwise use page.*
      const useOptions = Boolean(params?.fromModifiedDate || params?.toModifiedDate);
      if (useOptions) {
        queryParams.append('options.skip', skip.toString());
        queryParams.append('options.count', count.toString());
        queryParams.append('options.needTotalCount', 'true');
        if (params?.fromModifiedDate) queryParams.append('options.fromModifiedDate', params.fromModifiedDate);
        if (params?.toModifiedDate) queryParams.append('options.toModifiedDate', params.toModifiedDate);
      } else {
        queryParams.append('page.skip', skip.toString());
        queryParams.append('page.count', count.toString());
        queryParams.append('page.needTotalCount', 'true');
      }

      if (params?.fromDate) queryParams.append('fromDate', params.fromDate);
      if (params?.toDate) queryParams.append('toDate', params.toDate);
      if (params?.typeId) queryParams.append('typeId', params.typeId.toString());
      if (params?.providerId) queryParams.append('providerId', params.providerId.toString());
      if (params?.patientId) queryParams.append('patientId', params.patientId.toString());
      if (params?.status) params.status.forEach(s => queryParams.append('status', s));

      const queryString = queryParams.toString();
      const endpoint = `/appointments${queryString ? `?${queryString}` : ''}`;

      try {
        console.log(`Fetching appointments page: skip=${skip}, total so far=${allAppointments.length}`);
        const response = await this.request<ApiResponse<Appointment4D>>(endpoint);
        const items = Array.isArray((response as any)?.Items) ? (response as any).Items : [];
        const totalCount = typeof (response as any)?.TotalCount === 'number' ? (response as any).TotalCount as number : undefined;

        if (items.length > 0) {
          allAppointments = allAppointments.concat(items);
          skip += items.length;
          console.log(`Retrieved ${items.length} appointments. Total: ${allAppointments.length}${totalCount ? ` / ${totalCount}` : ''}`);
          // Keep going if API signaled more (TotalCount) or if we filled the requested page size
          const moreByCount = items.length === count;
          const moreByTotal = typeof totalCount === 'number' ? skip < totalCount : false;
          hasMore = moreByCount || moreByTotal;
          if (!hasMore) {
            console.log(`Last page reached (items=${items.length}, count=${count}, total=${totalCount ?? 'unknown'}).`);
          }
        } else {
          console.log(`No appointments returned. Stopping pagination at total=${allAppointments.length}`);
          hasMore = false;
        }
      } catch (error) {
        console.error(`❌ ERROR fetching appointments page with skip=${skip}:`, error);
        console.error(`⚠️ Stopping pagination early. Only retrieved ${allAppointments.length} appointments so far.`);
        hasMore = false;
      }
    }
    return allAppointments;
  }

  async getWebhookLogs() {
    const { data, error } = await supabase
      .from('webhook_logs')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  }

}

// Create a singleton instance
let apiInstance: FourDApi | null = null;

export const initializeApi = (credentials: {
  clientId: string;
  clientSecret: string;
  subscriptionKey: string;
  endpointUrl?: string;
}) => {
  apiInstance = new FourDApi(credentials);
  return apiInstance;
};

export const getApiInstance = () => {
  if (!apiInstance) {
    throw new Error('API not initialized. Call initializeApi first.');
  }
  return apiInstance;
};

export async function getStaff(): Promise<Staff4D[]> {
  const response = await fetch('/api/staff');
  if (!response.ok) {
    throw new Error('Failed to fetch staff');
  }
  const data = await response.json();
  return data.Items;
}

export async function getWebhookLogs(typeFilter?: string) {
  let query = supabase
    .from('webhook_logs')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(200);

  if (typeFilter && typeFilter !== 'all') {
    if (typeFilter === 'lead_notes') {
      query = query.in('type', ['lead_note_new', 'lead_note_update']);
    } else if (typeFilter === 'quotes') {
      query = query.in('type', ['quote_new', 'quote_update']);
    } else if (typeFilter === 'appointments') {
      query = query.in('type', ['appointment_new', 'appointment_update']);
    } else if (typeFilter === 'patients') {
      query = query.in('type', ['patient_new', 'patient_update']);
    } else {
      query = query.eq('type', typeFilter);
    }
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
}